export function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

export function escapeAttr(value: string): string {
  return escapeHtml(value);
}

export function primaryButton(id: string, label: string, type: "button" | "submit" = "button"): string {
  return `<button type="${type}" class="btn-primary" id="${escapeAttr(id)}">${escapeHtml(label)}</button>`;
}

export function quietButton(id: string, label: string): string {
  return `<button type="button" class="btn-quiet" id="${escapeAttr(id)}">${escapeHtml(label)}</button>`;
}

export function confirmDialog(opts: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  alt?: { prompt: string; label: string };
}): Promise<"confirm" | "alt" | null> {
  return new Promise((resolve) => {
    const dialog = document.createElement("dialog");
    dialog.className = "cap-dialog confirm-dialog";
    dialog.innerHTML = `
      <h3>${escapeHtml(opts.title)}</h3>
      <p class="hint">${escapeHtml(opts.body)}</p>
      <div class="confirm-actions">
        <button type="button" class="${opts.danger ? "btn-danger" : "btn-primary"}" data-confirm>${escapeHtml(opts.confirmLabel)}</button>
        <button type="button" class="btn-quiet" data-cancel>${escapeHtml(opts.cancelLabel || "Close")}</button>
      </div>
      ${
        opts.alt
          ? `<p class="confirm-alt">${escapeHtml(opts.alt.prompt)} <button type="button" class="link-btn" data-alt>${escapeHtml(opts.alt.label)}</button></p>`
          : ""
      }`;
    let answer: "confirm" | "alt" | null = null;
    dialog.querySelector("[data-confirm]")?.addEventListener("click", () => {
      answer = "confirm";
      dialog.close();
    });
    dialog.querySelector("[data-alt]")?.addEventListener("click", () => {
      answer = "alt";
      dialog.close();
    });
    dialog.querySelector("[data-cancel]")?.addEventListener("click", () => dialog.close());
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog) dialog.close();
    });
    dialog.addEventListener("close", () => {
      dialog.remove();
      resolve(answer);
    });
    document.body.appendChild(dialog);
    dialog.showModal();
  });
}

export function dangerButton(id: string, label: string): string {
  return `<button type="button" class="btn-danger" id="${escapeAttr(id)}">${escapeHtml(label)}</button>`;
}

export const ICONS = {
  caret: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>`,
  back: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M15 18l-6-6 6-6"/></svg>`,
  check: `<svg class="ico-svg ico-check" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" aria-hidden="true"><path d="M5 12l5 5L20 7"/></svg>`,
  clock: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>`,
  x: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M7 7l10 10M17 7L7 17"/></svg>`,
  external: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M14 5h5v5M19 5l-9 9"/><path d="M19 13v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1h5"/></svg>`,
  edit: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M4 20h4l10-10-4-4L4 16v4z"/><path d="M12 6l4 4"/></svg>`,
  logout: `<svg class="ico-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M10 7V6a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1h-8a1 1 0 0 1-1-1v-1"/><path d="M4 12h10M11 9l3 3-3 3"/></svg>`,
};

export function walletBar(address: string): string {
  const short = address.length < 12 ? address : `${address.slice(0, 6)}…${address.slice(-4)}`;
  return `<div class="wallet-bar">
      <span class="wallet-dot" aria-hidden="true"></span>
      <span class="wallet-bar-status">Your wallet Connected</span>
      <span class="wallet-bar-address" title="${escapeAttr(address)}">${escapeHtml(short)}</span>
      <button type="button" class="icon-btn wallet-bar-out" id="sign-out-wallet" aria-label="Disconnect wallet" title="Disconnect wallet">${ICONS.logout}</button>
    </div>`;
}

function withWallet(body: string, wallet: string | null | undefined): { body: string; footer: string } {
  if (!wallet) return { body, footer: "" };
  const start = body.indexOf('<div class="chip-row">');
  const end = start < 0 ? -1 : body.indexOf("</div>", start);
  if (end < 0) return { body, footer: walletBar(wallet) };
  const at = end + "</div>".length;
  return { body: `${body.slice(0, at)}${walletBar(wallet)}${body.slice(at)}`, footer: "" };
}

export function paymentShell(opts: {
  title: string;
  subtitle?: string;
  body: string;
  logoUrl?: string | null;
  brandName?: string | null;
  wallet?: string | null;
}): string {
  const customLogo = Boolean(opts.logoUrl);
  const logo = opts.logoUrl || "/logo.svg";
  const brand = opts.brandName || "Flowgate";
  const { body, footer } = withWallet(opts.body, opts.wallet);
  return `
    <div class="page">
      <div class="shell">
        <div class="pay-card">
          <header class="pay-head">
            <div class="pay-brand">
              <img class="pay-logo${customLogo ? " is-custom" : ""}" src="${escapeAttr(logo)}" alt="${escapeAttr(brand)}" />
              ${customLogo ? "" : `<span class="pay-brand-name">${escapeHtml(brand)}</span>`}
            </div>
            <div class="pay-head-text">
              <p class="pay-head-title">${escapeHtml(opts.title)}</p>
              ${opts.subtitle ? `<p class="pay-head-sub">${escapeHtml(opts.subtitle)}</p>` : ""}
            </div>
          </header>
          <div class="pay-body">${body}</div>
          ${footer}
        </div>
      </div>
    </div>`;
}

export function loadingShell(message: string): string {
  return `
    <div class="page">
      <div class="shell">
        <div class="pay-card pay-card-loading">
          <div class="pay-body">${loadingRow(message)}</div>
        </div>
      </div>
    </div>`;
}

export function amountHero(amount: string, caption: string): string {
  return `
    <div class="amount-hero">
      <p class="amount-value">${escapeHtml(amount)}</p>
      <p class="amount-caption">${escapeHtml(caption)}</p>
    </div>`;
}

export function summaryList(rows: Array<{ label: string; value: string; html?: string }>): string {
  if (!rows.length) return "";
  return `<dl class="summary">${rows
    .map(
      (row) =>
        `<div class="summary-row"><dt>${escapeHtml(row.label)}</dt><dd>${row.html ?? escapeHtml(row.value)}</dd></div>`
    )
    .join("")}</dl>`;
}

export function stepDots(total: number, current: number): string {
  const dots = Array.from({ length: total }, (_, i) => {
    const on = i < current ? " is-on" : "";
    return `<span class="step-dot${on}"></span>`;
  }).join("");
  return `<div class="step-dots">${dots}</div>`;
}

export function chip(iconUrl: string, label: string): string {
  return `<span class="chip"><img src="${escapeAttr(iconUrl)}" alt="" />${escapeHtml(label)}</span>`;
}

export function chipRow(items: Array<{ icon: string; label: string }>): string {
  if (!items.length) return "";
  return `<div class="chip-row">${items
    .map((item, i) => `${i ? `<span class="chip-sep">${ICONS.caret}</span>` : ""}${chip(item.icon, item.label)}`)
    .join("")}</div>`;
}

export function backButton(id: string, label: string): string {
  return `<button type="button" class="back-btn" id="${escapeAttr(id)}">${ICONS.back} Back to ${escapeHtml(label)}</button>`;
}

export function pickButton(opts: {
  key: string;
  value: string;
  icon?: string;
  iconHtml?: string;
  title: string;
  subtitle?: string;
  iconClass?: string;
}): string {
  const media =
    opts.iconHtml ||
    `<img class="pick-ico${opts.iconClass ? ` ${opts.iconClass}` : ""}" src="${escapeAttr(opts.icon || "")}" alt="" />`;
  return `
    <button type="button" class="pick" data-${escapeAttr(opts.key)}="${escapeAttr(opts.value)}">
      ${media}
      <span class="pick-text">
        <strong>${escapeHtml(opts.title)}</strong>
        ${opts.subtitle ? `<small>${escapeHtml(opts.subtitle)}</small>` : ""}
      </span>
      ${ICONS.caret}
    </button>`;
}

const MSG_ICON = {
  danger: `<svg class="msg-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 7.5v5.5M12 16.5v.01"/></svg>`,
  info: `<svg class="msg-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5.5M12 7.5v.01"/></svg>`,
};

export function errorBox(message: string): string {
  return `<div class="msg msg-danger" role="alert">${MSG_ICON.danger}<span>${escapeHtml(message)}</span></div>`;
}

export function infoBox(message: string): string {
  return `<div class="msg msg-info">${MSG_ICON.info}<span>${escapeHtml(message)}</span></div>`;
}

export function hint(message: string): string {
  return `<p class="hint">${escapeHtml(message)}</p>`;
}

export function chargeQuote(amount: string): string {
  return `<p class="charge-quote">You will be charged <strong>${escapeHtml(amount)}</strong></p>`;
}

export function textField(opts: {
  id: string;
  label: string;
  type?: string;
  value?: string;
  placeholder?: string;
  autocomplete?: string;
  required?: boolean;
}): string {
  const type = opts.type || "text";
  const required = opts.required ? " required" : "";
  return `
    <div class="field">
      <label for="${escapeAttr(opts.id)}">${escapeHtml(opts.label)}</label>
      <input
        id="${escapeAttr(opts.id)}"
        name="${escapeAttr(opts.id)}"
        type="${escapeAttr(type)}"
        value="${escapeAttr(opts.value || "")}"
        placeholder="${escapeAttr(opts.placeholder || "")}"
        autocomplete="${escapeAttr(opts.autocomplete || "on")}"
        ${required}
      />
    </div>`;
}

type PendingKind = "wallet" | "chain" | "work";

const PENDING_ICON: Record<PendingKind, string> = {
  wallet: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7.5A2.5 2.5 0 0 1 6.5 5H18v3"/><path d="M4 7.5v9A2.5 2.5 0 0 0 6.5 19H20V8H6.5A2.5 2.5 0 0 1 4 7.5z"/><circle cx="16" cy="13.5" r="1.2" fill="currentColor" stroke="none"/></svg>`,
  chain: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l7.5 4.2v9.6L12 21l-7.5-4.2V7.2z"/><path d="M4.5 7.2L12 11.4l7.5-4.2M12 11.4V21"/></svg>`,
  work: "",
};

const PENDING_NOTE: Record<PendingKind, string> = {
  wallet: "Continue in your wallet. Nothing is sent until you confirm there.",
  chain: "This usually takes under a minute. Keep this window open.",
  work: "",
};

function pendingKind(title: string): PendingKind {
  if (/in your wallet|opening wallet|connect a wallet/i.test(title)) return "wallet";
  if (/blockchain|confirmation/i.test(title)) return "chain";
  return "work";
}

/** A status card. A second line after "\n" is quoted below the title (for example the terms being signed). */
export function loadingRow(label: string): string {
  const [title, ...rest] = label.split("\n");
  const detail = rest.join(" ").trim();
  const kind = pendingKind(title);
  const note = PENDING_NOTE[kind];
  const mark = kind === "work" ? `<span class="pending-spin"></span>` : PENDING_ICON[kind];
  return `<div class="pending pending-${kind}" role="status" aria-live="polite">
      <div class="pending-mark" aria-hidden="true">${mark}</div>
      <div class="pending-text">
        <p class="pending-title">${escapeHtml(title)}</p>
        ${note ? `<p class="pending-note">${escapeHtml(note)}</p>` : ""}
      </div>
      ${detail ? `<p class="pending-detail">${escapeHtml(detail)}</p>` : ""}
      <div class="pending-bar" aria-hidden="true"><span></span></div>
    </div>`;
}

export function successBlock(opts: {
  amount: string;
  caption: string;
  title: string;
  subtitle: string;
  chips?: string;
}): string {
  return `
    <div class="success">
      <div class="success-mark">${ICONS.check}</div>
      ${amountHero(opts.amount, opts.caption)}
      ${opts.chips || ""}
      <h2 class="success-title">${escapeHtml(opts.title)}</h2>
      <p class="success-sub">${escapeHtml(opts.subtitle)}</p>
    </div>`;
}

export function networkLabel(name: string, shortName: string, testnet?: boolean): string {
  const title = name.trim();
  const short = shortName.trim();
  const base = title && short && title !== short ? `${title} (${short})` : title || short;
  return testnet ? `${base} · Testnet` : base;
}
